import { CloneConversationType, useTranslation } from "@next-platform/client-common-core";
import { Button, ButtonSize, ButtonVariant, Input, Modal, RadioGroup } from "@next-platform/client-common-spicy-ui";
import { useCallback, useEffect, useMemo, useState } from "react";

import Trans from "@/components/Trans/Trans";

type CloneConversationModalProps = {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onSubmit: (cloneType: CloneConversationType, cloneTitle: string) => void;
  onCancel?: () => void;
  isLoading?: boolean;
};

export default function CloneConversationModal({ isOpen, onOpenChange, isLoading = false, onSubmit, onCancel }: CloneConversationModalProps) {
  const { t } = useTranslation();
  const [stateCloneConversationType, setStateCloneConversationType] = useState<CloneConversationType>(CloneConversationType.PARTIAL);
  const [stateCloneTitle, setStateCloneTitle] = useState("");

  const resetFormState = useCallback(() => {
    setStateCloneTitle("");
    setStateCloneConversationType(CloneConversationType.PARTIAL);
  }, []);

  const cloneConversationOptions = useMemo(
    () => [
      { label: t("conversation:modal.cloneConversation.option.partial"), value: CloneConversationType.PARTIAL },
      { label: t("conversation:modal.cloneConversation.option.full"), value: CloneConversationType.FULL },
    ],
    [t],
  );

  useEffect(() => {
    if (isOpen) {
      resetFormState();
    }
  }, [isOpen, resetFormState]);

  const handleClose = useCallback(() => {
    onCancel?.();
    resetFormState();
    onOpenChange(false);
  }, [onCancel, onOpenChange, resetFormState]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        handleClose();
      }
    },
    [handleClose],
  );

  const handleCloneTypeChange = useCallback((value: string) => {
    setStateCloneConversationType(value as CloneConversationType);
  }, []);

  const handleOnSubmit = useCallback(() => {
    onSubmit(stateCloneConversationType, stateCloneTitle);
  }, [onSubmit, stateCloneConversationType, stateCloneTitle]);

  return (
    <Modal title={t("common:cloneConversation")} open={isOpen} onOpenChange={handleOpenChange} dismissable={!isLoading} className="gap-md">
      <div data-testid="CloneConversationModal-RadioGroup">
        <RadioGroup
          name="cloneConversationType"
          value={stateCloneConversationType}
          onValueChange={handleCloneTypeChange}
          items={cloneConversationOptions}
          horizontal
          className="flex !justify-start py-0.5"
        />
      </div>
      <Input
        label={t("common:conversationTitle")}
        value={stateCloneTitle}
        onChange={setStateCloneTitle}
        placeholder={t("chat:modal.cloneConversation.input.placeholder")}
        maxLength={50}
        showMaxLengthLabel={false}
        readOnly={isLoading}
        required
      />
      <div className="flex justify-end gap-1.5">
        <Button variant={ButtonVariant.OUTLINED} onClick={handleClose} size={ButtonSize.LARGE} disabled={isLoading}>
          <Trans i18nKey="common:cancel" />
        </Button>
        <Button onClick={handleOnSubmit} size={ButtonSize.LARGE} disabled={!stateCloneTitle?.trim()} loading={isLoading}>
          <Trans i18nKey="common:clone" />
        </Button>
      </div>
    </Modal>
  );
}
